<script setup>
import { computed, ref, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import addMonths from 'date-fns/addMonths';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfDay from 'date-fns/endOfDay';
import endOfMonth from 'date-fns/endOfMonth';
import format from 'date-fns/format';
import isAfter from 'date-fns/isAfter';
import parse from 'date-fns/parse';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import subDays from 'date-fns/subDays';
import { vOnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const emit = defineEmits(['rangeTypeChange', 'monthOffsetChange']);

const fromModel = defineModel('from', { type: Date, default: null });
const toModel = defineModel('to', { type: Date, default: null });
const daysNumModel = defineModel('daysNum', { type: Number, default: null });

const { t, locale } = useI18n();

const DATE_FILTER_TYPES = {
  DAY: 'day',
  MONTH: 'month',
  CUSTOM: 'custom',
};

const DATE_FILTER_ACTION = 'select_date_range';

const dayMenuItemConfigs = computed(() => [
  {
    label: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS'),
    value: 'last_7_days',
    action: DATE_FILTER_ACTION,
    type: DATE_FILTER_TYPES.DAY,
    daysBefore: 6,
  },
  {
    label: t('REPORT.DATE_RANGE_OPTIONS.LAST_14_DAYS'),
    value: 'last_14_days',
    action: DATE_FILTER_ACTION,
    type: DATE_FILTER_TYPES.DAY,
    daysBefore: 13,
  },
  {
    label: t('REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS'),
    value: 'last_30_days',
    action: DATE_FILTER_ACTION,
    type: DATE_FILTER_TYPES.DAY,
    daysBefore: 29,
  },
]);

const resolvedLocale = computed(() => {
  const currentLocale =
    locale.value ||
    (typeof navigator !== 'undefined' ? navigator.language : 'en');
  return currentLocale.replace('_', '-');
});

const monthFormatter = computed(
  () =>
    new Intl.DateTimeFormat(resolvedLocale.value, {
      month: 'long',
      year: 'numeric',
    })
);

const monthMenuItemConfigs = computed(() => {
  const now = new Date();
  const offsets = [0, -1, -2];

  return offsets.map(offset => ({
    label:
      offset === 0
        ? t('REPORT.DATE_RANGE_OPTIONS.THIS_MONTH')
        : monthFormatter.value.format(addMonths(now, offset)),
    value: offset === 0 ? 'this_month' : `month_${offset}`,
    action: DATE_FILTER_ACTION,
    type: DATE_FILTER_TYPES.MONTH,
    monthOffset: offset,
  }));
});

const customMenuItemConfig = computed(() => ({
  label: t('REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE'),
  value: 'custom',
  action: DATE_FILTER_ACTION,
  type: DATE_FILTER_TYPES.CUSTOM,
}));

const selectedDateRangeValue = ref('');
const showCustomPanel = ref(false);
const customStartDate = ref('');
const customEndDate = ref('');
const customDateError = ref('');

const [showDropdown, toggleDropdown] = useToggle();
const monthOffset = ref(0);

const menuItems = computed(() => {
  const selectedValue = selectedDateRangeValue.value;
  return [
    ...dayMenuItemConfigs.value,
    ...monthMenuItemConfigs.value,
    { ...customMenuItemConfig.value, isSelected: selectedValue === 'custom' },
  ].map(config => ({
    ...config,
    isSelected: selectedValue === config.value,
  }));
});

selectedDateRangeValue.value = menuItems.value[0]?.value || '';

const menuSections = computed(() => {
  const dayItems = menuItems.value.filter(
    item => item.type === DATE_FILTER_TYPES.DAY
  );
  const monthItems = menuItems.value.filter(
    item => item.type === DATE_FILTER_TYPES.MONTH
  );
  const customItems = menuItems.value.filter(
    item => item.type === DATE_FILTER_TYPES.CUSTOM
  );

  return [
    { items: dayItems },
    { items: monthItems },
    { items: customItems },
  ].filter(section => section.items.length > 0);
});

const selectedConfig = computed(
  () =>
    menuItems.value.find(
      menuItem => menuItem.value === selectedDateRangeValue.value
    ) || menuItems.value[0]
);

const customRangeLabel = computed(() => {
  if (
    selectedDateRangeValue.value !== 'custom' ||
    !fromModel.value ||
    !toModel.value
  ) {
    return t('REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE');
  }
  return `${format(fromModel.value, 'dd/MM/yyyy')} – ${format(toModel.value, 'dd/MM/yyyy')}`;
});

const selectedLabel = computed(() => {
  if (selectedDateRangeValue.value === 'custom') return customRangeLabel.value;
  const selectedItem = menuItems.value.find(
    item => item.value === selectedDateRangeValue.value
  );
  return selectedItem?.label || '';
});

const computeRange = config => {
  if (!config) {
    return null;
  }

  if (config.type === DATE_FILTER_TYPES.MONTH) {
    const now = new Date();
    const baseMonthStart = startOfMonth(addMonths(now, monthOffset.value));
    const from = startOfDay(baseMonthStart);
    const isCurrentMonth =
      config.value === 'this_month' && monthOffset.value === 0;
    const to = isCurrentMonth
      ? endOfDay(now)
      : endOfDay(endOfMonth(baseMonthStart));
    const daysBefore = differenceInCalendarDays(to, from);
    return { from, to, daysBefore };
  }

  if (config.type === DATE_FILTER_TYPES.CUSTOM) {
    return null;
  }

  const to = endOfDay(new Date());
  const from = startOfDay(subDays(to, Number(config.daysBefore)));
  return { from, to, daysBefore: Number(config.daysBefore) };
};

const applySelection = config => {
  if (!config || config.type === DATE_FILTER_TYPES.CUSTOM) return;

  if (config.type === DATE_FILTER_TYPES.MONTH) {
    monthOffset.value = config.monthOffset || 0;
  } else {
    monthOffset.value = 0;
  }

  const range = computeRange(config);
  if (!range) return;

  const { from, to, daysBefore } = range;
  fromModel.value = from;
  toModel.value = to;
  daysNumModel.value = daysBefore;

  emit('rangeTypeChange', config.type);
  emit('monthOffsetChange', monthOffset.value);
};

const applyCustomRange = () => {
  if (!customStartDate.value || !customEndDate.value) {
    customDateError.value = t('REPORT.CUSTOM_DATE_RANGE.INVALID_RANGE');
    return;
  }

  const from = startOfDay(
    parse(customStartDate.value, 'yyyy-MM-dd', new Date())
  );
  const to = endOfDay(parse(customEndDate.value, 'yyyy-MM-dd', new Date()));

  if (isAfter(from, to)) {
    customDateError.value = t('REPORT.CUSTOM_DATE_RANGE.INVALID_RANGE');
    return;
  }

  customDateError.value = '';
  const daysBefore = differenceInCalendarDays(to, from);

  fromModel.value = from;
  toModel.value = to;
  daysNumModel.value = daysBefore;
  monthOffset.value = 0;

  showCustomPanel.value = false;
  emit('rangeTypeChange', DATE_FILTER_TYPES.CUSTOM);
  emit('monthOffsetChange', 0);
};

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  if (action !== DATE_FILTER_ACTION) {
    return;
  }

  if (value === 'custom') {
    selectedDateRangeValue.value = 'custom';
    customDateError.value = '';
    showCustomPanel.value = true;
    return;
  }

  showCustomPanel.value = false;
  selectedDateRangeValue.value = value;
};

const handleClickOutside = () => {
  toggleDropdown(false);
  showCustomPanel.value = false;
};

watch(
  () => selectedConfig.value,
  config => {
    applySelection(config);
  },
  { immediate: true }
);
</script>

<template>
  <div
    v-on-click-outside="handleClickOutside"
    class="relative flex items-center group"
  >
    <Button
      sm
      slate
      faded
      :label="selectedLabel"
      class="rounded-md group-hover:bg-n-alpha-2"
      @click="toggleDropdown()"
    />
    <DropdownMenu
      v-if="showDropdown"
      :menu-items="menuItems"
      :menu-sections="menuSections"
      class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full"
      @action="handleAction($event)"
    />
    <div
      v-if="showCustomPanel"
      class="absolute top-full mt-1 ltr:right-0 rtl:left-0 z-50 bg-n-solid-2 border border-n-weak rounded-xl shadow-lg p-4 flex flex-col gap-3 min-w-[260px]"
    >
      <p class="text-sm font-medium text-n-slate-12">
        {{ $t('REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE') }}
      </p>
      <div class="flex flex-col gap-2">
        <label class="text-xs text-n-slate-11">
          {{ $t('DATE_PICKER.DATE_RANGE_INPUT.START') }}
        </label>
        <input
          v-model="customStartDate"
          type="date"
          :max="customEndDate || undefined"
          class="!text-sm !mb-0 w-full"
        />
      </div>
      <div class="flex flex-col gap-2">
        <label class="text-xs text-n-slate-11">
          {{ $t('DATE_PICKER.DATE_RANGE_INPUT.END') }}
        </label>
        <input
          v-model="customEndDate"
          type="date"
          :min="customStartDate || undefined"
          class="!text-sm !mb-0 w-full"
        />
      </div>
      <p v-if="customDateError" class="text-xs text-r-500">
        {{ customDateError }}
      </p>
      <div class="flex gap-2 justify-end">
        <Button
          sm
          slate
          faded
          :label="$t('REPORT.CUSTOM_DATE_RANGE.CANCEL')"
          class="rounded-md"
          @click="showCustomPanel = false"
        />
        <Button
          sm
          :label="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
          class="rounded-md"
          @click="applyCustomRange"
        />
      </div>
    </div>
  </div>
</template>
